import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { createApiClient } from '@fittrack/services';
import { createThemeService, type ApplicationTheme } from '@fittrack/services/src/themeService';

export interface ThemeContextValue {
  theme: ApplicationTheme | null;
  cssVariables: string;
  loading: boolean;
  error: string | null;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export interface ThemeProviderProps {
  children: ReactNode;
  applicationType?: 'Admin' | 'Online' | 'Both';
  baseURL: string;
  apiKey?: string | null; // Optional: for authenticated requests (FitTrackReact)
  siteId?: number | null; // Optional: for explicit siteId passing (FitTrackOnline URL-based)
  tenantId?: string; // Optional: for tenant identification
  requireAuth?: boolean; // If true, only load theme when apiKey is available (FitTrackReact)
  onError?: (error: string) => void; // Optional error callback
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  applicationType = 'Both',
  baseURL,
  apiKey = null,
  siteId = null,
  tenantId,
  requireAuth = false,
  onError
}) => {
  const [theme, setTheme] = useState<ApplicationTheme | null>(null);
  const [cssVariables, setCssVariables] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadTheme = async () => {
    // If requireAuth is true and no apiKey, skip loading (for FitTrackReact)
    if (requireAuth && !apiKey) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create API client with optional authentication
      const apiClientConfig: Parameters<typeof createApiClient>[0] = {
        baseURL,
        tenantId,
      };

      // Add apiKey if provided (for authenticated requests)
      if (apiKey) {
        apiClientConfig.apiKey = apiKey;
      }

      // Add siteId if provided (for explicit siteId passing)
      if (siteId) {
        apiClientConfig.siteId = siteId;
      }

      const apiClient = createApiClient(apiClientConfig);
      const themeService = createThemeService(apiClient);
      const response = await themeService.getActiveTheme(applicationType);

      console.log('[DEBUG] Theme API response:', JSON.stringify(response, null, 2));

      // The API response is double-wrapped: response.data contains the actual API response
      // which has its own data property with theme and css
      if (response.success && response.data) {
        // Check if response.data has the expected structure
        const themeData = (response.data as any).data || response.data;

        if (themeData.theme && themeData.css) {
          setTheme(themeData.theme);
          setCssVariables(themeData.css);
          applyTheme(themeData.css);
        } else {
          throw new Error('Invalid theme response structure');
        }
      } else {
        // Use default theme if no active theme
        const errorMsg = 'No active theme found, using defaults';
        console.warn(errorMsg);
        if (requireAuth) {
          setError(errorMsg);
        }
        if (onError) {
          onError(errorMsg);
        }
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load theme';
      console.error('Error loading theme:', err);

      if (requireAuth) {
        setError(errorMsg);
      } else {
        // For public endpoints (FitTrackOnline), silently fail and use defaults
        console.warn('Theme system not available, using defaults:', errorMsg);
      }

      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (css: string) => {
    console.log('[DEBUG] applyTheme called, CSS length:', css.length);
    console.log('[DEBUG] CSS contains --page-background-color:', css.includes('--page-background-color'));

    // Remove existing theme style if it exists
    const existingStyle = document.getElementById('fittrack-theme');
    if (existingStyle) {
      console.log('[DEBUG] Removing existing theme style');
      existingStyle.remove();
    }

    // Create new style element with theme CSS
    const styleElement = document.createElement('style');
    styleElement.id = 'fittrack-theme';
    styleElement.textContent = css;
    document.head.appendChild(styleElement);

    console.log('[DEBUG] Style element appended');
    console.log('[DEBUG] Computed --page-background-color:',
      window.getComputedStyle(document.documentElement).getPropertyValue('--page-background-color').trim());
  };

  useEffect(() => {
    loadTheme();
  }, [applicationType, apiKey, siteId, baseURL, tenantId]); // Reload if any of these change

  const value: ThemeContextValue = {
    theme,
    cssVariables,
    loading,
    error,
    refreshTheme: loadTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};